import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Eye, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  useConfigByGrupo, useUpdateConfig, previewPlantilla,
} from "@/features/config/api";
import type { ApiError } from "@/lib/api";
import { openBlobInNewTab } from "@/lib/pdf";

const VARIABLES_DISPONIBLES: { key: string; desc: string }[] = [
  { key: "{{ isp.nombre_legal }}", desc: "Razón social del ISP" },
  { key: "{{ isp.nombre_comercial }}", desc: "Nombre comercial" },
  { key: "{{ isp.rfc }}", desc: "RFC del ISP" },
  { key: "{{ isp.domicilio_fiscal }}", desc: "Domicilio fiscal" },
  { key: "{{ isp.telefono }}", desc: "Teléfono" },
  { key: "{{ isp.email }}", desc: "Email" },
  { key: "{{ isp.representante_legal }}", desc: "Representante legal" },
  { key: "{{ cliente.razon_social }}", desc: "Razón social del cliente" },
  { key: "{{ cliente.rfc }}", desc: "RFC del cliente" },
  { key: "{{ cliente.codigo }}", desc: "Código del cliente" },
  { key: "{{ cliente.direccion_instalacion }}", desc: "Domicilio de instalación" },
  { key: "{{ contrato.codigo }}", desc: "Código del contrato" },
  { key: "{{ contrato.fecha_corte }}", desc: "Día de corte" },
  { key: "{{ contrato.ip_asignada }}", desc: "IP asignada" },
  { key: "{{ contrato.pppoe_user }}", desc: "Usuario PPPoE" },
  { key: "{{ plan.nombre }}", desc: "Nombre del plan" },
  { key: "{{ plan.velocidad_down_mbps }}", desc: "Velocidad bajada (Mbps)" },
  { key: "{{ plan.velocidad_up_mbps }}", desc: "Velocidad subida (Mbps)" },
  { key: "{{ plan.precio }}", desc: "Precio del plan (MXN)" },
  { key: "{{ fecha_hoy }}", desc: "Fecha actual (DD/MM/YYYY)" },
];

export function PlantillaContratoSection() {
  const { data: items, isLoading } = useConfigByGrupo("contrato");
  const updateMut = useUpdateConfig();
  const [plantilla, setPlantilla] = useState<string>("");
  const [dirty, setDirty] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (items) {
      const item = items.find((i) => i.key === "contrato.plantilla_html");
      setPlantilla(typeof item?.value === "string" ? item.value : "");
      setDirty(false);
    }
  }, [items]);

  async function handleSave() {
    try {
      await updateMut.mutateAsync({
        key: "contrato.plantilla_html",
        value: plantilla,
        value_type: "string",
        grupo: "contrato",
      });
      toast.success("Plantilla guardada");
      setDirty(false);
    } catch (e) {
      toast.error("Error al guardar", {
        description: (e as ApiError).message,
      });
    }
  }

  async function handlePreview() {
    setPreviewing(true);
    try {
      const blob = await previewPlantilla(plantilla);
      openBlobInNewTab(blob);
    } catch (e) {
      toast.error("Error al generar preview", {
        description: (e as Error).message,
      });
    } finally {
      setPreviewing(false);
    }
  }

  function insertVariable(v: string) {
    setPlantilla((prev) => prev + v);
    setDirty(true);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
        <AlertCircle size={14} className="mt-0.5 shrink-0" />
        <div>
          La plantilla usa sintaxis <b>Jinja2</b> sobre HTML. Las variables entre{" "}
          <code>{"{{ ... }}"}</code> se reemplazan por los datos reales al generar el
          PDF del contrato. Usa el panel de la derecha para copiarlas. El <b>Preview</b>{" "}
          genera un PDF con datos de ejemplo (o del primer contrato real, si hay).
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_260px]">
        <Textarea
          value={plantilla}
          onChange={(e) => { setPlantilla(e.target.value); setDirty(true); }}
          rows={28}
          className="font-mono text-[11px]"
          spellCheck={false}
        />

        <div className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold">Variables disponibles</h3>
          <p className="text-[10px] text-muted-foreground">
            Clic para insertar al final del editor.
          </p>
          <div className="flex max-h-[620px] flex-col gap-0.5 overflow-y-auto rounded border p-1">
            {VARIABLES_DISPONIBLES.map((v) => (
              <button
                key={v.key}
                type="button"
                onClick={() => insertVariable(v.key)}
                className="flex flex-col items-start gap-0 rounded px-1.5 py-1 text-left text-[10px] hover:bg-accent"
              >
                <code className="font-mono">{v.key}</code>
                <span className="text-muted-foreground">{v.desc}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        {dirty && (
          <span className="text-xs text-muted-foreground">Cambios sin guardar</span>
        )}
        <Button size="sm" variant="outline" onClick={handlePreview} disabled={previewing}>
          <Eye size={12} />
          {previewing ? "Generando…" : "Ver preview PDF"}
        </Button>
        <Button size="sm" onClick={handleSave} disabled={!dirty || updateMut.isPending}>
          <Save size={12} />
          {updateMut.isPending ? "Guardando…" : "Guardar plantilla"}
        </Button>
      </div>
    </div>
  );
}
